import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Button, Flex, Input, Select, Tabs, message } from "antd"
import { Icon } from "ui/Icon"
import { fetchExperiences } from "store/experiences"
import ExperienceModal from "./ExperienceModal"
import styles from "./WebsiteIntegration.module.css"
import useActiveGuide from "hooks/useActiveClient"
import { Text } from "ui/Typography"
import { useTheme } from "context/AntdThemeContext"
import { colors } from "themes/variables"
import { AiChatbotWidget } from "./AiChatbotWidget"
import { isFeatureEnabled } from "config/features"

const StepNumber = ({ number }) => (
  <div className={styles.stepNumber}>
    <p className={styles.stepNumberText}>{number}</p>
  </div>
)

const StepSection = ({ number, title, subtitle, children, icon }) => (
  <div className={styles.stepSection}>
    <StepNumber number={number} />
    <div className={styles.stepContent}>
      <div className={styles.stepTitleRow}>
        <p className={styles.stepTitle}>{title}</p>
        {icon}
      </div>
      <p className={styles.stepSubtitle}>{subtitle}</p>
      <div className={styles.stepBody}>{children}</div>
    </div>
  </div>
)

const normalizeHexColor = (value) => {
  const raw = String(value || "").trim()
  if (!raw) return ""
  const withHash = raw.startsWith("#") ? raw : `#${raw}`
  if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(withHash)) return ""
  if (withHash.length === 4) {
    return `#${withHash[1]}${withHash[1]}${withHash[2]}${withHash[2]}${withHash[3]}${withHash[3]}`.toUpperCase()
  }
  if (withHash.length === 5) {
    return `#${withHash[1]}${withHash[1]}${withHash[2]}${withHash[2]}${withHash[3]}${withHash[3]}${withHash[4]}${withHash[4]}`.toUpperCase()
  }
  return withHash.toUpperCase()
}

const isCompleteHexColor = (value) => /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(String(value || "").trim())

const getReadableTextColor = (backgroundColor) => {
  const hex = normalizeHexColor(backgroundColor)
  if (!hex) return "#FFFFFF"

  const r = Number.parseInt(hex.slice(1, 3), 16)
  const g = Number.parseInt(hex.slice(3, 5), 16)
  const b = Number.parseInt(hex.slice(5, 7), 16)
  const alpha = hex.length === 9 ? Number.parseInt(hex.slice(7, 9), 16) / 255 : 1
  const blendedR = Math.round(alpha * r + (1 - alpha) * 255)
  const blendedG = Math.round(alpha * g + (1 - alpha) * 255)
  const blendedB = Math.round(alpha * b + (1 - alpha) * 255)
  const yiq = (blendedR * 299 + blendedG * 587 + blendedB * 114) / 1000

  return yiq >= 128 ? "#111111" : "#FFFFFF"
}

export const WebsiteIntegration = () => {
  const [bookingType, setBookingType] = useState("all")
  const [buttonLabel, setButtonLabel] = useState("Book Now")
  const { isSportsman } = useTheme()
  const defaultButtonColor = isSportsman ? colors.sportsmanBrand : colors.sportswomanBrand
  const [buttonColor, setButtonColor] = useState(defaultButtonColor)
  const [hexColorInput, setHexColorInput] = useState(defaultButtonColor.toUpperCase())
  const [buttonSize, setButtonSize] = useState("medium")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedSpecificExperience, setSelectedSpecificExperience] = useState("")
  const dispatch = useDispatch()
  const [t] = useTranslation()
  const { list: experiences, loading } = useSelector((state) => state.experiences)
  const user = useSelector((store) => store.user)
  const { isActive: active } = useActiveGuide()
  const appOrigin = window.location?.origin || ""
  const guideUuid = user?.guide?.uuid || ""
  const isSpecificExperience = bookingType === "specific"
  const isCopyEnabled = (isSpecificExperience && !selectedSpecificExperience) || !guideUuid

  const buttonTextColor = getReadableTextColor(buttonColor)
  let previewButtonSizeClass = styles.previewButtonMedium
  if (buttonSize === "small") {
    previewButtonSizeClass = styles.previewButtonSmall
  } else if (buttonSize === "large") {
    previewButtonSizeClass = styles.previewButtonLarge
  }
  const isHexColorValid = isCompleteHexColor(hexColorInput)
  const showHexValidationError = hexColorInput.trim().length > 1 && !isHexColorValid

  const escapeHtmlAttribute = (value) =>
    String(value || "")
      .replaceAll("&", "&amp;")
      .replaceAll('"', "&quot;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")

  const safeButtonLabel = escapeHtmlAttribute(buttonLabel)
  const safeButtonColor = escapeHtmlAttribute(buttonColor)
  const safeButtonTextColor = escapeHtmlAttribute(buttonTextColor)
  const encodedGuideUuid = encodeURIComponent(guideUuid)
  const encodedBookingType = encodeURIComponent(bookingType)
  const encodedExperienceId = encodeURIComponent(selectedSpecificExperience)
  const encodedButtonLabel = encodeURIComponent(buttonLabel)
  const encodedButtonColor = encodeURIComponent(buttonColor)
  const encodedButtonTextColor = encodeURIComponent(buttonTextColor)
  const encodedButtonSize = encodeURIComponent(buttonSize)
  const widgetScriptSrc = `${appOrigin}/booking-widget.js?buttonId=dsg-booking-widget-btn&guideId=${encodedGuideUuid}&bookingType=${encodedBookingType}&experienceId=${encodedExperienceId}&buttonLabel=${encodedButtonLabel}&buttonColor=${encodedButtonColor}&buttonTextColor=${encodedButtonTextColor}&buttonSize=${encodedButtonSize}`

  const code = `<button
  id="dsg-booking-widget-btn"
    style="background-color:${safeButtonColor};color:${safeButtonTextColor};"
>
  ${safeButtonLabel}
</button>
  <script src="${widgetScriptSrc}" defer></script>`

  const handleHexColorChange = (event) => {
    const value = (event.target.value || "").toUpperCase()
    const stripped = value.replaceAll("#", "")
    const prefixed = `#${stripped}`
    const sanitized = prefixed.replaceAll(/[^#0-9A-F]/g, "")
    setHexColorInput(sanitized)

    if (isCompleteHexColor(sanitized)) {
      const normalized = normalizeHexColor(sanitized.trim())
      setButtonColor(normalized)
    }
  }

  const handleHexColorBlur = () => {
    const normalized = normalizeHexColor(hexColorInput)
    if (normalized) {
      setButtonColor(normalized)
      setHexColorInput(normalized)
      return
    }

    // Revert to last valid color when the typed value is invalid.
    setHexColorInput(buttonColor.toUpperCase())
  }

  const handleCopy = () => {
    navigator.clipboard
      .writeText(code)
      .then(() => message.success(t("webIntegration.copy_success")))
      .catch(() => message.error(t("webIntegration.copy_failure")))
  }

  useEffect(() => {
    dispatch(fetchExperiences())
  }, [dispatch])

  useEffect(() => {
    setHexColorInput(buttonColor.toUpperCase())
  }, [buttonColor])

  const hostedBookingUrl = (() => {
    if (!guideUuid) return ""
    if (isSpecificExperience && selectedSpecificExperience) {
      return `${appOrigin}/#/book/${encodedGuideUuid}/experience/${encodeURIComponent(selectedSpecificExperience)}`
    }
    if (!isSpecificExperience) {
      return `${appOrigin}/#/book/${encodedGuideUuid}`
    }
    return ""
  })()

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(hostedBookingUrl)
      .then(() => message.success(t("webIntegration.copy_success")))
      .catch(() => message.error(t("webIntegration.copy_failure")))
  }

  const tabItems = [
    {
      key: "booking",
      label: t("webIntegration.booking_widget"),
      children: (
        <>
          <div className={styles.buttonGroup}>
            <Button
              type="primary"
              className={`${styles.noShadow} ${bookingType === "all" ? styles.buttonActive : styles.buttonInactive}`}
              rootClassName={styles.roundedActionButton}
              onClick={() => {
                setBookingType("all")
                setSelectedSpecificExperience("")
              }}
            >
              {t("webIntegration.all_experience_booking")}
            </Button>
            <Button
              type="primary"
              className={`${styles.noShadow} ${isSpecificExperience ? styles.buttonActive : styles.buttonInactive}`}
              rootClassName={styles.roundedActionButton}
              onClick={() => setBookingType("specific")}
            >
              {t("webIntegration.specific_experience_booking")}
            </Button>
          </div>

          <StepSection
            number={1}
            title={t("webIntegration.button_label_title")}
            subtitle={t("webIntegration.button_label_subtitle")}
            icon={<Icon type="feather" name="Info" width={20} height={20} onClick={() => setIsModalOpen(true)} />}
          >
            <label htmlFor="website-integration-button-label" className={styles.fieldLabel}>
              {t("webIntegration.button_label_field")}
            </label>
            <Input
              id="website-integration-button-label"
              placeholder={t("webIntegration.button_label_placeholder")}
              className={`${styles.responsiveField} ${styles.wideField}`}
              value={buttonLabel}
              onChange={(e) => setButtonLabel(e.target.value)}
            />
          </StepSection>
          <StepSection number={2} title="Customize Button Appearance" subtitle="Choose your button color and size, then preview it live.">
            <div className={styles.customizationRow}>
              <div className={styles.fieldColumn}>
                <Flex gap={8}>
                  <Flex vertical className={styles.colorControls}>
                    <label htmlFor="website-integration-button-color-hex" className={styles.fieldLabel}>
                      Button color
                    </label>
                    <Flex gap={8} align="center">
                      <input
                        aria-label="Button color picker"
                        type="color"
                        value={buttonColor}
                        onChange={(e) => {
                          const nextColor = e.target.value.toUpperCase()
                          setButtonColor(nextColor)
                          setHexColorInput(nextColor)
                        }}
                      />
                      <Input
                        id="website-integration-button-color-hex"
                        className={styles.hexInput}
                        value={hexColorInput}
                        onChange={handleHexColorChange}
                        onBlur={handleHexColorBlur}
                        status={showHexValidationError ? "error" : ""}
                        placeholder="#1A2B3C or #1A2B3C80"
                      />
                    </Flex>
                  </Flex>
                  <div className={styles.previewContainer}>
                    <Text className={styles.previewLabel}>Live preview</Text>
                    <button
                      type="button"
                      className={`${styles.previewButton} ${previewButtonSizeClass}`}
                      style={{
                        "--preview-button-bg": buttonColor,
                        "--preview-button-text": buttonTextColor
                      }}
                    >
                      {buttonLabel || "Book Now"}
                    </button>
                  </div>
                </Flex>

                {showHexValidationError && (
                  <div className={styles.fieldMessage}>
                    <Text color="red">Enter a valid hex color with # prefix (e.g. #1A2B3C or #1A2B3C80).</Text>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.fieldColumn}>
              <label htmlFor="website-integration-button-size" className={styles.fieldLabel}>
                Button size
              </label>
              <Select
                id="website-integration-button-size"
                className={`${styles.responsiveField} ${styles.sizeField}`}
                value={buttonSize}
                onChange={(value) => setButtonSize(value)}
                options={[
                  { label: "Small", value: "small" },
                  { label: "Medium", value: "medium" },
                  { label: "Large", value: "large" }
                ]}
              />
            </div>
          </StepSection>
          {isSpecificExperience && (
            <StepSection
              number={3}
              title={t("webIntegration.select_experience_title")}
              subtitle={t("webIntegration.select_experience_subtitle")}
            >
              <label htmlFor="website-integration-select-experience" className={styles.fieldLabel}>
                {t("webIntegration.select_experience_label")}
              </label>
              <Select
                id="website-integration-select-experience"
                showSearch
                placeholder="Select Experience"
                className={`${styles.responsiveField} ${styles.wideField}`}
                loading={loading}
                options={experiences?.map((exp) => ({
                  label: exp.title,
                  value: exp.uuid
                }))}
                onChange={(value) => {
                  setSelectedSpecificExperience(value)
                }}
                filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
              />
            </StepSection>
          )}
          <StepSection
            number={bookingType === "all" ? 3 : 4}
            title={t("webIntegration.copy_code_title")}
            subtitle={t("webIntegration.copy_code_subtitle")}
          >
            <div className={`${styles.codeBlockContainer} mb-m`}>
              <div className={styles.codeBlockInner}>
                <Button
                  color="primary"
                  variant="outlined"
                  icon={<Icon type="feather" name="Copy" width={16} height={16} />}
                  onClick={active && !isCopyEnabled ? handleCopy : undefined}
                  disabled={!active || isCopyEnabled}
                >
                  {t("webIntegration.copy_code_button")}
                </Button>
              </div>
              {isCopyEnabled && <Text color="red">{t("webIntegration.copy_code_error")}</Text>}
            </div>
          </StepSection>
          <StepSection
            number={bookingType === "all" ? 4 : 5}
            title="Booking Link (Alternative)"
            subtitle="For platforms like Squarespace or Wix where the embed script may not work, use this direct link behind a Book Now button on your website instead."
          >
            <div className={`${styles.codeBlockContainer} mb-m`}>
              <div className={styles.codeBlockInner}>
                <Button
                  color="primary"
                  variant="outlined"
                  icon={<Icon type="feather" name="Copy" width={16} height={16} />}
                  onClick={active && !isCopyEnabled ? handleCopyLink : undefined}
                  disabled={!active || isCopyEnabled}
                >
                  Copy Link
                </Button>
              </div>
              {hostedBookingUrl && (
                <div style={{ marginTop: 10, backgroundColor: "var(--color-primary-surface)", borderRadius: 6, padding: "8px 12px" }}>
                  <p className={styles.codeBlockShortText} style={{ wordBreak: "break-all", margin: 0 }}>
                    {hostedBookingUrl}
                  </p>
                </div>
              )}
              {isCopyEnabled && <Text color="red">{t("webIntegration.copy_code_error")}</Text>}
            </div>
          </StepSection>
          <ExperienceModal bookingType={bookingType} buttonName={buttonLabel} open={isModalOpen} onCancel={() => setIsModalOpen(false)} />
        </>
      )
    },
    ...(isFeatureEnabled("AI_CHATBOT_WIDGET")
      ? [{ key: "chatbot", label: t("webIntegration.ai_chatbot_widget"), children: <AiChatbotWidget /> }]
      : [])
  ]

  return <Tabs defaultActiveKey="booking" items={tabItems} />
}

StepNumber.propTypes = {
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}

StepSection.propTypes = {
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  icon: PropTypes.node
}

StepSection.defaultProps = {
  icon: null
}
